import React from 'react';
import type { FC, PropsWithChildren } from 'react';
import cn from 'classnames';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';

import type { TProgressBarV2 } from './models';

export const ProgressBarV2: FC<PropsWithChildren<TProgressBarV2>> = ({
    Grid,
    HatchingProgressBar,
    SteppedProgressBar,
    Text,
    backgroundColor,
    caption,
    captionText,
    children,
    className,
    currentStepName,
    dataTestId = 'progress-bar',
    hatchValue = 0,
    header,
    inverted = false,
    line = 'stepper',
    maxStep = 0,
    nextStepName,
    step,
    steps,
    styles,
    value = 0,
    view = 'positive',
}) => {
    const { captionContent } = useNamedContainers(children);

    const isInverted = inverted ? 'dark' : 'light';
    const isNextStep = header === 'nextStep';
    const stepName = isNextStep ? nextStepName : currentStepName;

    const isStepperProgressBar = line === 'stepper' && String(maxStep);
    const isHatchingProgressBar = line === 'hatching' && String(hatchValue) && String(value);

    const headerSlot = (
        <Grid
            colAutoWidth
            columnsMargin={isNextStep ? undefined : 4}
            justify={isNextStep ? 'between' : 'start'}
            styler={{ marginBottom: 8 }}
        >
            <Text styler={{ margin: 0 }} theme={isInverted} view="primary-small">
                {steps}
            </Text>

            <Text styler={{ margin: 0 }} theme={isInverted} view="primary-small">
                {header !== 'steps' && stepName}
            </Text>
        </Grid>
    );

    const captionSlots = {
        captionFromSlot: captionContent,
        captionText: (
            <Text
                kind="caption"
                styler={{ marginBottom: 0, marginTop: 8 }}
                theme={isInverted}
                view="primary-small"
            >
                {captionText}
            </Text>
        ),
    };

    const currentCaption =
        (captionContent && captionSlots.captionFromSlot) || captionSlots.captionText;

    return (
        <div className={className}>
            {headerSlot}
            {isStepperProgressBar && (
                <SteppedProgressBar
                    colors={inverted ? 'inverted' : 'default'}
                    dataTestId={dataTestId}
                    maxStep={maxStep}
                    step={step}
                    view={view}
                />
            )}
            {isHatchingProgressBar && (
                <HatchingProgressBar
                    className={cn(
                        backgroundColor && [styles[`${backgroundColor}BackgroundColor`]],
                        {
                            [styles.hatchingInverted]: inverted,
                        }
                    )}
                    dataTestId={dataTestId}
                    hatchValue={hatchValue}
                    value={value}
                    view={view}
                />
            )}
            {caption && currentCaption}
        </div>
    );
};
